import { motion } from 'framer-motion'
import PropTypes from 'prop-types'
import { Link } from 'react-router-dom'

import chartTeaserDark from 'assets/images/chart-teaser__dark.svg'
import chartTeaserLight from 'assets/images/chart-teaser__light.svg'
import Badge from 'components/common/Badge'
import Button from 'components/common/Button'
import { offcanvasVariables } from 'components/common/OffCanvas'
import Text from 'components/common/Text'
import TrackedExternalLink from 'components/common/TrackedExternalLink'
import theme from 'theme'

export default function AttackMetricsChartTeaser({ darkMode, ...rest }) {
  const bgColor = darkMode ? theme.colors.steel[2] : theme.colors.white[0]
  const bgUrl = darkMode ? chartTeaserDark : chartTeaserLight
  return (
    <motion.div
      data-test-attack-metrics-chart-teaser
      animate={{ opacity: 1 }}
      css={[
        {
          position: 'relative',
          height: 430,
          background: `${bgColor} url(${bgUrl}) no-repeat center top`,
          backgroundSize: 'contain'
        },

        // This is used to collapse content spacing normally provided by `<OffCanvas />`
        darkMode && {
          marginTop: -offcanvasVariables(theme).padding,
          marginLeft: -offcanvasVariables(theme).padding,
          marginRight: -offcanvasVariables(theme).padding
        }
      ]}
      exit={{ opacity: 0 }}
      initial={{ opacity: 0 }}
      {...rest}
    >
      <div
        css={{
          width: '50%',
          background: darkMode
            ? theme.boxShadowColors.light
            : 'rgba(255, 255, 255, 0.72)',
          borderRadius: theme.borderRadius[1],
          padding: theme.space[4],
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          zIndex: theme.zIndices[1]
        }}
      >
        <Text
          as="h3"
          css={{
            color: darkMode ? theme.colors.white[0] : theme.colors.steel[2],
            textAlign: 'center',
            marginBottom: theme.space[3],
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center'
          }}
        >
          <Badge
            backgroundGradient={[theme.colors.steel[1], theme.colors.steel[1]]}
            css={{
              marginRight: theme.space[2]
            }}
          >
            NEW
          </Badge>
          Real time Attack Visualizations
        </Text>
        <Text
          as="h4"
          css={{
            color: darkMode ? theme.colors.white[0] : theme.colors.steel[2],
            textAlign: 'center',
            fontWeight: theme.fontWeights[0],
            marginBottom: theme.space[2]
          }}
        >
          Watch the impact of your attacks and monitor them in real time with
          our new attack visualization graphs.
        </Text>
        <Text
          as="p"
          css={{
            color: darkMode ? theme.colors.white[0] : theme.colors.steel[2],
            fontSize: theme.fontSizes[0]
          }}
        >
          Gremlin will collect and store a limited set of attack metrics to
          generate the visualizations. Learn more about our{' '}
          <TrackedExternalLink
            css={{
              color: darkMode ? theme.colors.white[0] : theme.colors.steel[2],
              textDecoration: 'underline',

              '&:hover, &:focus': {
                color: darkMode ? theme.colors.white[0] : theme.colors.steel[2]
              }
            }}
            href="https://www.gremlin.com/privacy"
          >
            data collection policies here
          </TrackedExternalLink>
        </Text>
        <Button
          primary
          as={Link}
          css={{
            color: theme.colors.white[0],
            marginTop: theme.space[4],
            justifyContent: 'center',
            width: '100%'
          }}
          to="/settings/preferences"
        >
          Enable
        </Button>
      </div>
    </motion.div>
  )
}

AttackMetricsChartTeaser.propTypes = {
  darkMode: PropTypes.bool
}
